import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const TABS = [
  { key: 'new', label: 'New Customer' },
  { key: 'existing', label: 'Existing Customer' },
];

/**
 * Segmented control at the top of the Contact form that switches between
 * "New Customer" and "Existing Customer" variants.
 *
 * Extracted from ContactUsPage.jsx in the Feb 2026 P2 refactor.
 */
export function CustomerTypeTabs({ value, onChange }) {
  return (
    <div
      role="tablist"
      aria-label="Customer type"
      className="grid grid-cols-2 gap-1 p-1 bg-muted rounded-lg"
      data-testid="contact-customer-type-tabs"
    >
      {TABS.map((tab) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.key)}
            data-testid={`contact-tab-${tab.key}`}
            className={
              'py-2.5 px-4 rounded-md text-sm font-medium transition-colors ' +
              (active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground')
            }
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * "Project Title / Architect" + "Specifier's Location" — always visible on
 * both customer types (both required).
 */
export function ProjectContextFields({ values, errors, onChange }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="projectTitle">Project Title / Architect *</Label>
        <Input
          id="projectTitle"
          value={values.projectTitle}
          onChange={(e) => onChange('projectTitle', e.target.value)}
          placeholder="Cypress Grove Elementary — Perkins & Will"
          className={errors.projectTitle ? 'border-destructive' : ''}
          data-testid="contact-project-title-input"
        />
        {errors.projectTitle && (
          <p className="text-xs text-destructive">{errors.projectTitle}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="specifiersLocation">Specifier&apos;s Location *</Label>
        <Input
          id="specifiersLocation"
          value={values.specifiersLocation}
          onChange={(e) => onChange('specifiersLocation', e.target.value)}
          placeholder="Charlotte, NC"
          className={errors.specifiersLocation ? 'border-destructive' : ''}
          data-testid="contact-specifiers-location-input"
        />
        {errors.specifiersLocation && (
          <p className="text-xs text-destructive">{errors.specifiersLocation}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Existing-customer-only fields — Customer Account Number + Account
 * Representative. Renders nothing when the "New Customer" tab is active.
 */
export function ExistingCustomerFields({ visible, values, errors, onChange }) {
  if (!visible) return null;
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid="contact-existing-customer-fields"
    >
      <div className="space-y-2">
        <Label htmlFor="customerAccountNumber">Customer Account Number *</Label>
        <Input
          id="customerAccountNumber"
          value={values.customerAccountNumber}
          onChange={(e) => onChange('customerAccountNumber', e.target.value)}
          placeholder="e.g. PS-10234"
          className={errors.customerAccountNumber ? 'border-destructive' : ''}
          data-testid="contact-account-number-input"
        />
        {errors.customerAccountNumber && (
          <p className="text-xs text-destructive">{errors.customerAccountNumber}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="accountRepresentative">Account Representative *</Label>
        <Input
          id="accountRepresentative"
          value={values.accountRepresentative}
          onChange={(e) => onChange('accountRepresentative', e.target.value)}
          placeholder="Your Portico rep's name (if known)"
          className={errors.accountRepresentative ? 'border-destructive' : ''}
          data-testid="contact-account-rep-input"
        />
        {errors.accountRepresentative && (
          <p className="text-xs text-destructive">{errors.accountRepresentative}</p>
        )}
      </div>
    </div>
  );
}
